import React, { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import {
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  Paper,
  CircularProgress,
  Box,
  Switch,
  FormControlLabel,
  IconButton,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  ClickAwayListener,
  Popover
} from '@mui/material';
import { AddCircleOutline, DeleteOutline, ColorLens as ColorLensIcon } from '@mui/icons-material';
import { ChromePicker } from 'react-color';
import { toast } from 'react-toastify';
import { getSectionContent, updateSectionContent, uploadImage } from '../services/contentService';

const resolvePath = (object, path, defaultValue = null) => {
  const keys = Array.isArray(path) ? path : path.split('.');
  let current = object;
  for (const key of keys) {
    if (current === null || typeof current !== 'object' || !current.hasOwnProperty(key)) {
      return defaultValue;
    }
    current = current[key];
  }
  return current;
};

const initialSectionDataState = {
  mainTitle: 'Our Services',
  mainSubtitle: 'Explore what we offer',
  mainTitleColor: '#333333',
  mainTitleFontSize: 36,
  mainTitleFontFamily: 'Arial',
  mainTitleFontWeight: '700',
  mainSubtitleColor: '#555555',
  mainSubtitleFontSize: 20,
  mainSubtitleFontFamily: "Roboto",
  mainSubtitleFontWeight: '400',
  defaultCardTitleColor: '#333333',
  defaultCardTitleFontSize: 22,
  defaultCardTitleFontFamily: 'Arial',
  defaultCardTitleFontWeight: '600',
  defaultCardDescriptionColor: '#666666',
  defaultCardDescriptionFontSize: 16,
  defaultCardDescriptionFontFamily: 'Arial',
  defaultCardDescriptionFontWeight: '400',
  defaultCardIconColor: '#007bff',
  defaultCardIconBackgroundColor: '#e7f3ff',
  serviceCards: [
    {
      id: 'service1',
      icon: 'FaCog', // Default Icon
      title: "Service One",
      titleColor: "#444444",
      titleFontSize: 24,
      titleFontFamily: "Montserrat",
      titleFontWeight: '600',
      description: "Detailed description for service one.",
      descriptionColor: "#666666",
      descriptionFontSize: 16,
      descriptionFontFamily: "Lato",
      descriptionFontWeight: '400',
      link: "/service-one"
    },
    {
      id: 'service2',
      icon: 'FaStar',
      title: "Service Two",
      titleColor: "#444444",
      titleFontSize: 24,
      titleFontFamily: "Montserrat",
      titleFontWeight: '600',
      description: "Detailed description for service two.",
      descriptionColor: "#666666",
      descriptionFontSize: 16,
      descriptionFontFamily: "Lato",
      descriptionFontWeight: '400',
      link: "/service-two"
    },
    {
      id: 'service3',
      icon: 'FaHeart',
      title: "Service Three",
      titleColor: "#444444",
      titleFontSize: 24,
      titleFontFamily: "Montserrat",
      titleFontWeight: '600',
      description: "Detailed description for service three.",
      descriptionColor: "#666666",
      descriptionFontSize: 16,
      descriptionFontFamily: "Lato",
      descriptionFontWeight: '400',
      link: "/service-three"
    }
  ],
  longServiceData: {
    title: 'Detailed Service Information',
    description: 'More about our specialized services.',
    imageUrl: '',
    imageAlt: 'Service Image',
    titleColor: '#333333',
    titleFontSize: 28,
    titleFontFamily: 'Arial',
    titleFontWeight: '700',
    textColor: '#555555',
    textFontSize: 16,
    textFontFamily: 'Arial',
    textFontWeight: '400',
    backgroundColor: '#f8f9fa'
  },
};

const commonFontFamilies = [
  'Arial', 'Verdana', 'Georgia', 'Times New Roman', 'Courier New', 'Lucida Console', 'Helvetica', 'Roboto', 'Open Sans', 'Lato', 'Montserrat'
];

const commonFontWeights = [
  { value: '100', label: '100 - Thin' },
  { value: '200', label: '200 - Extra Light' },
  { value: '300', label: '300 - Light' },
  { value: '400', label: '400 - Normal' },
  { value: '500', label: '500 - Medium' },
  { value: '600', label: '600 - Semi Bold' },
  { value: '700', label: '700 - Bold' },
  { value: '800', label: '800 - Extra Bold' },
  { value: '900', label: '900 - Black' },
];

const iconOptions = [
  { value: 'FaBeer', label: 'Beer Mug' }, { value: 'FaCog', label: 'Cog Wheel' }, { value: 'FaStar', label: 'Star' }, { value: 'FaHeart', label: 'Heart' }, { value: 'FaLightbulb', label: 'Lightbulb' }, { value: 'FaRocket', label: 'Rocket' }, { value: 'FaTools', label: 'Tools' }, { value: 'FaShieldAlt', label: 'Shield' }, { value: 'FaWrench', label: 'Wrench' }, { value: 'FaShoppingCart', label: 'Shopping Cart' }, { value: 'FaCamera', label: 'Camera' }, { value: 'FaPaintBrush', label: 'Paint Brush' }, { value: 'FaCode', label: 'Code' }, { value: 'FaChartLine', label: 'Chart Line' }, { value: 'FaBullhorn', label: 'Bullhorn' }, { value: 'FaUsers', label: 'Users' }, { value: 'FaBriefcase', label: 'Briefcase' }, { value: 'FaHome', label: 'Home' }, { value: 'FaCar', label: 'Car' }, { value: 'FaMotorcycle', label: 'Motorcycle' }, { value: 'FaTruck', label: 'Truck' }, { value: 'FaShip', label: 'Ship' }, { value: 'FaPlane', label: 'Plane' }, { value: 'FaBuilding', label: 'Building' }, { value: 'FaStore', label: 'Store' }, { value: 'FaUniversity', label: 'University' }, { value: 'FaHospital', label: 'Hospital' }, { value: 'FaMedkit', label: 'Medkit' }, { value: 'FaLeaf', label: 'Leaf' }, { value: 'FaFire', label: 'Fire' }, { value: 'FaSun', label: 'Sun' }, { value: 'FaMoon', label: 'Moon' }, { value: 'FaCloud', label: 'Cloud' }, { value: 'FaBolt', label: 'Bolt' }, { value: 'FaMusic', label: 'Music' }, { value: 'FaFilm', label: 'Film' }, { value: 'FaBook', label: 'Book' }, { value: 'FaGift', label: 'Gift' }, { value: 'FaKey', label: 'Key' }, { value: 'FaLock', label: 'Lock' }, { value: 'FaUnlock', label: 'Unlock' }, { value: 'FaCreditCard', label: 'Credit Card' }, { value: 'FaMoneyBillWave', label: 'Money Bill' }, { value: 'FaDollarSign', label: 'Dollar Sign' }, { value: 'FaEuroSign', label: 'Euro Sign' }, { value: 'FaPoundSign', label: 'Pound Sign' }, { value: 'FaYenSign', label: 'Yen Sign' }, { value: 'FaBitcoin', label: 'Bitcoin' }, { value: 'FaMapMarkerAlt', label: 'Map Marker' }, { value: 'FaPhone', label: 'Phone' }, { value: 'FaEnvelope', label: 'Envelope' }, { value: 'FaGlobe', label: 'Globe' }, { value: 'FaPrint', label: 'Print' }, { value: 'FaDownload', label: 'Download' }, { value: 'FaUpload', label: 'Upload' }, { value: 'FaShare', label: 'Share' }, { value: 'FaLink', label: 'Link' }, { value: 'FaCut', label: 'Cut' }, { value: 'FaCopy', label: 'Copy' }, { value: 'FaPaste', label: 'Paste' }, { value: 'FaSave', label: 'Save' }, { value: 'FaTrashAlt', label: 'Trash' }, { value: 'FaEdit', label: 'Edit' }, { value: 'FaCheck', label: 'Check' }, { value: 'FaTimes', label: 'Times' }, { value: 'FaPlus', label: 'Plus' }, { value: 'FaMinus', label: 'Minus' }, { value: 'FaInfoCircle', label: 'Info Circle' }, { value: 'FaQuestionCircle', label: 'Question Circle' }, { value: 'FaExclamationTriangle', label: 'Exclamation Triangle' }, { value: 'FaExclamationCircle', label: 'Exclamation Circle' }, { value: 'FaThumbsUp', label: 'Thumbs Up' }, { value: 'FaThumbsDown', label: 'Thumbs Down' }, { value: 'FaComment', label: 'Comment' }, { value: 'FaComments', label: 'Comments' }, { value: 'FaUser', label: 'User' }, { value: 'FaUserCircle', label: 'User Circle' }, { value: 'FaUserFriends', label: 'User Friends' }, { value: 'FaUsersCog', label: 'Users Cog' }, { value: 'FaCalendarAlt', label: 'Calendar' }, { value: 'FaClock', label: 'Clock' }, { value: 'FaHistory', label: 'History' }, { value: 'FaSearch', label: 'Search' }, { value: 'FaFilter', label: 'Filter' }, { value: 'FaSort', label: 'Sort' }, { value: 'FaSyncAlt', label: 'Sync' }, { value: 'FaRedoAlt', label: 'Redo' }, { value: 'FaUndoAlt', label: 'Undo' }, { value: 'FaSignOutAlt', label: 'Sign Out' }, { value: 'FaSignInAlt', label: 'Sign In' }, { value: 'FaExternalLinkAlt', label: 'External Link' }, { value: 'FaDesktop', label: 'Desktop' }, { value: 'FaLaptop', label: 'Laptop' }, { value: 'FaTabletAlt', label: 'Tablet' }, { value: 'FaMobileAlt', label: 'Mobile' }, { value: 'FaDatabase', label: 'Database' }, { value: 'FaServer', label: 'Server' }, { value: 'FaNetworkWired', label: 'Network Wired' }, { value: 'FaWifi', label: 'Wifi' }, { value: 'FaBluetooth', label: 'Bluetooth' }, { value: 'FaUsb', label: 'USB' }, { value: 'FaSdCard', label: 'SD Card' }, { value: 'FaHdd', label: 'HDD' }, { value: 'FaKeyboard', label: 'Keyboard' }, { value: 'FaMouse', label: 'Mouse' }, { value: 'FaHeadphones', label: 'Headphones' }, { value: 'FaMicrophone', label: 'Microphone' }, { value: 'FaVolumeUp', label: 'Volume Up' }, { value: 'FaVolumeDown', label: 'Volume Down' }, { value: 'FaVolumeMute', label: 'Volume Mute' }, { value: 'FaPlay', label: 'Play' }, { value: 'FaPause', label: 'Pause' }, { value: 'FaStop', label: 'Stop' }, { value: 'FaForward', label: 'Forward' }, { value: 'FaBackward', label: 'Backward' }, { value: 'FaStepForward', label: 'Step Forward' }, { value: 'FaStepBackward', label: 'Step Backward' }, { value: 'FaEject', label: 'Eject' }, { value: 'FaExpand', label: 'Expand' }, { value: 'FaCompress', label: 'Compress' }, { value: 'FaRandom', label: 'Random' }, { value: 'FaRetweet', label: 'Retweet' }, { value: 'FaArrowsAlt', label: 'Arrows Alt' }, { value: 'FaArrowsAltH', label: 'Arrows Alt H' }, { value: 'FaArrowsAltV', label: 'Arrows Alt V' }, { value: 'FaExpandArrowsAlt', label: 'Expand Arrows Alt' }, { value: 'FaCompressArrowsAlt', label: 'Compress Arrows Alt' },
  // Add more as needed
];

const fontWeightMap = {
  'font-thin': '100',
  'font-extralight': '200',
  'font-light': '300',
  'font-normal': '400',
  'font-medium': '500',
  'font-semibold': '600',
  'font-bold': '700',
  'font-extrabold': '800',
  'font-black': '900',
  // Add direct numeric strings as well in case data is mixed
  '100': '100', '200': '200', '300': '300', '400': '400', '500': '500',
  '600': '600', '700': '700', '800': '800', '900': '900',
};

const transformFetchedData = (data, currentIconOptions) => {
  const newData = _.cloneDeep(data);

  // Transform top-level font weights
  if (newData.mainTitleFontWeight) {
    newData.mainTitleFontWeight = fontWeightMap[newData.mainTitleFontWeight] || newData.mainTitleFontWeight;
  }
  if (newData.mainSubtitleFontWeight) {
    newData.mainSubtitleFontWeight = fontWeightMap[newData.mainSubtitleFontWeight] || newData.mainSubtitleFontWeight;
  }

  // Transform servicesList
  if (newData.serviceCards && Array.isArray(newData.serviceCards)) {
    newData.serviceCards = newData.serviceCards.map(service => {
      const newService = { ...service }; // Shallow copy is fine for individual service object
      if (newService.titleFontWeight) {
        newService.titleFontWeight = fontWeightMap[newService.titleFontWeight] || newService.titleFontWeight;
      }
      if (newService.descriptionFontWeight) {
        newService.descriptionFontWeight = fontWeightMap[newService.descriptionFontWeight] || newService.descriptionFontWeight;
      }
      if (newService.icon) {
        const iconExists = currentIconOptions.some(opt => opt.value === newService.icon);
        if (!iconExists) {
          const prefixedIcon = `Fa${newService.icon}`;
          if (currentIconOptions.some(opt => opt.value === prefixedIcon)) {
            newService.icon = prefixedIcon;
          } else {
            // Fallback to the first available icon or an empty string if no match is found
            newService.icon = currentIconOptions[0]?.value || ''; 
          }
        }
      }
      return newService;
    });
  }
  return newData;
};

const ServicesSectionEditor = () => {
  const sectionId = 'servicesSection';
  const [sectionData, setSectionData] = useState(() => JSON.parse(JSON.stringify(initialSectionDataState)));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Color Picker State
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerAnchorEl, setColorPickerAnchorEl] = useState(null);
  const [currentColorField, setCurrentColorField] = useState(null); // e.g., 'mainTitleColor' or 'longServiceData.backgroundColor'

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rawData = await getSectionContent(sectionId);
      let processedData = { ...sectionData, ...rawData }; // Merge fetched with initial defaults

      // Helper to parse and validate style fields
      const processStyleField = (obj, keyPrefix, defaultSize, defaultFamily, defaultWeight) => {
        const sizeKey = `${keyPrefix}FontSize`;
        const familyKey = `${keyPrefix}FontFamily`;
        const weightKey = `${keyPrefix}FontWeight`;

        // Font Size
        if (obj[sizeKey] !== undefined && obj[sizeKey] !== null) {
          if (typeof obj[sizeKey] === 'string') {
            const parsed = parseFloat(String(obj[sizeKey]).replace(/px/gi, ''));
            obj[sizeKey] = isNaN(parsed) ? defaultSize : parsed;
          } else if (typeof obj[sizeKey] !== 'number') {
            obj[sizeKey] = defaultSize;
          }
        } else {
          obj[sizeKey] = defaultSize;
        }
        // Font Family
        if (typeof obj[familyKey] !== 'string' || !obj[familyKey]) {
          obj[familyKey] = defaultFamily;
        }
        // Font Weight
        if (typeof obj[weightKey] !== 'string' || !obj[weightKey]) {
          obj[weightKey] = defaultWeight;
        }
      };

      // Process main title and subtitle styles
      processStyleField(processedData, 'mainTitle', 36, 'Arial', '700');
      processStyleField(processedData, 'mainSubtitle', 20, 'Arial', '400');

      // Process longServiceData styles
      if (processedData.longServiceData) {
        processStyleField(processedData.longServiceData, 'title', 28, 'Arial', '700');
        processStyleField(processedData.longServiceData, 'text', 16, 'Arial', '400');
      } else {
        // Initialize longServiceData with defaults if not present
        processedData.longServiceData = { ...sectionData.longServiceData };
        processStyleField(processedData.longServiceData, 'title', 28, 'Arial', '700');
        processStyleField(processedData.longServiceData, 'text', 16, 'Arial', '400');
      }
      
      // Process default card styles
      processStyleField(processedData, 'defaultCardTitle', 22, 'Arial', '600');
      processStyleField(processedData, 'defaultCardDescription', 16, 'Arial', '400');
      if (typeof processedData.defaultCardIconColor !== 'string' || !processedData.defaultCardIconColor) {
        processedData.defaultCardIconColor = sectionData.defaultCardIconColor;
      }
      if (typeof processedData.defaultCardIconBackgroundColor !== 'string' || !processedData.defaultCardIconBackgroundColor) {
        processedData.defaultCardIconBackgroundColor = sectionData.defaultCardIconBackgroundColor;
      }

      // Process individual serviceCards styles
      if (rawData && rawData.serviceCards && Array.isArray(rawData.serviceCards)) {
        processedData.serviceCards = rawData.serviceCards.map(card => {
          const processedCard = { ...card }; // Start with fetched card data
          
          processStyleField(processedCard, 'title', processedData.defaultCardTitleFontSize, processedData.defaultCardTitleFontFamily, processedData.defaultCardTitleFontWeight);
          processStyleField(processedCard, 'description', processedData.defaultCardDescriptionFontSize, processedData.defaultCardDescriptionFontFamily, processedData.defaultCardDescriptionFontWeight);

          // Ensure color fields exist, falling back to defaults if necessary
          if(typeof processedCard.titleColor !== 'string' || !processedCard.titleColor) {
            processedCard.titleColor = processedData.defaultCardTitleColor;
          }
          if(typeof processedCard.descriptionColor !== 'string' || !processedCard.descriptionColor) {
            processedCard.descriptionColor = processedData.defaultCardDescriptionColor;
          }
          if(typeof processedCard.iconColor !== 'string' || !processedCard.iconColor) {
            processedCard.iconColor = processedData.defaultCardIconColor;
          }
          if(typeof processedCard.iconBackgroundColor !== 'string' || !processedCard.iconBackgroundColor) {
            processedCard.iconBackgroundColor = processedData.defaultCardIconBackgroundColor;
          }
          return processedCard;
        });
      } else {
        processedData.serviceCards = []; // Ensure it's an array if not present in rawData
      }

      // Transform fetched data for font weights and icons
      const transformedData = transformFetchedData(processedData, iconOptions);
      setSectionData(transformedData);
    } catch (err) {
      console.error("Error fetching services section data:", err);
      setSectionData(JSON.parse(JSON.stringify(initialSectionDataState))); // Reset to default on error
      setError('Failed to load services data. Please try again.');
      toast.error('Failed to load services data.');
    } finally {
      setLoading(false);
    }
  }, [sectionId]); // Removed sectionData from dependency array to prevent re-fetch loops with defaults

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleColorPickerOpen = (event, fieldName) => {
    setColorPickerAnchorEl(event.currentTarget);
    setCurrentColorField(fieldName);
    setShowColorPicker(true);
  };

  const handleColorPickerClose = () => {
    setShowColorPicker(false);
    setColorPickerAnchorEl(null);
    setCurrentColorField(null);
  };

  const handleColorChange = (color) => {
    if (currentColorField) {
      const keys = currentColorField.split('.');
      setSectionData(prevData => {
        const newData = { ...prevData };
        let currentLevel = newData;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!currentLevel[keys[i]]) currentLevel[keys[i]] = {}; // Create nested object if it doesn't exist
          currentLevel = currentLevel[keys[i]];
        }
        currentLevel[keys[keys.length - 1]] = color.hex;
        return newData;
      });
    }
  };

  const handleInputChange = (event, explicitPath) => {
    const target = event.target || {}; // Ensure target is an object
    const targetName = target.name;
    const rawValue = target.value;
    const type = target.type;
    const checked = target.checked;

    // Determine the effective path for state update
    const effectivePath = explicitPath ? explicitPath : targetName;

    // Validate effectivePath
    if (typeof effectivePath !== 'string' || effectivePath.trim() === '') {
      // console.error('Error in handleInputChange: effectivePath is not a valid string.', { explicitPathArg: explicitPath, targetName: targetName, targetElement: target });
      return; // Early exit if path is invalid
    }

    let value = type === 'checkbox' ? checked : rawValue;

    // Special handling for font size and weight to ensure they are stored correctly
    if (effectivePath.endsWith('FontSize')) {
      const numValue = parseInt(value, 10);
      value = isNaN(numValue) ? '' : numValue; // Store as number or empty string if invalid
    } else if (effectivePath.endsWith('FontWeight')) {
      // Font weight is expected to be a string (e.g., '400', '700') as per commonFontWeights
      value = String(value); 
    }
    
    // Debug log removed
    // console.log('[handleInputChange] Debug:', { path: effectivePath, val: value, eventType: type, eventName: targetName });

    setSectionData(prevData => {
      const newData = { ...prevData };
      _.set(newData, effectivePath, value); // Use lodash.set for deep updates
      return newData;
    });
  };

  const handleServiceCardChange = (index, event) => {
    const { name, value: rawValue, type, checked } = event.target;
    let value = type === 'checkbox' ? checked : rawValue;

    const isFontSizeField = name && name.endsWith('FontSize');

    if (isFontSizeField && type === 'number') {
      if (rawValue === '') {
        value = ''; // Allow temporarily empty
      } else {
        const parsed = parseFloat(rawValue);
        value = isNaN(parsed) ? rawValue : parsed;
      }
    }

    const updatedServiceCards = sectionData.serviceCards.map((card, i) => {
      if (i === index) {
        return { ...card, [name]: value };
      }
      return card;
    });
    setSectionData(prevData => ({ ...prevData, serviceCards: updatedServiceCards }));
  };

  const addServiceCard = () => {
    setSectionData(prevData => ({
      ...prevData,
      serviceCards: [
        ...prevData.serviceCards,
        {
          id: `new-${Date.now()}`,
          icon: '',
          title: 'New Service',
          description: 'Service description.',
          isVisible: true,
          displayOrder: prevData.serviceCards.length,
          titleColor: prevData.defaultCardTitleColor,
          titleFontSize: prevData.defaultCardTitleFontSize,
          titleFontFamily: prevData.defaultCardTitleFontFamily,
          titleFontWeight: prevData.defaultCardTitleFontWeight,
          descriptionColor: prevData.defaultCardDescriptionColor,
          descriptionFontSize: prevData.defaultCardDescriptionFontSize,
          descriptionFontFamily: prevData.defaultCardDescriptionFontFamily,
          descriptionFontWeight: prevData.defaultCardDescriptionFontWeight,
          iconColor: prevData.defaultCardIconColor,
          iconBackgroundColor: prevData.defaultCardIconBackgroundColor,
        }
      ]
    }));
  };

  const removeServiceCard = (index) => {
    setSectionData(prevData => ({
      ...prevData,
      serviceCards: prevData.serviceCards.filter((_, i) => i !== index)
    }));
  };
  
  const handleLongServiceImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('image', file);
      setSaving(true);
      try {
        const response = await uploadImage(formData); // Using generic uploadImage
        setSectionData(prevData => ({
          ...prevData,
          longServiceData: {
            ...prevData.longServiceData,
            imageUrl: response.imageUrl, // Assuming response contains imageUrl
          }
        }));
        toast.success('Long service image uploaded successfully!');
      } catch (err) {
        console.error('Error uploading long service image:', err);
        toast.error('Failed to upload long service image.');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateSectionContent(sectionId, sectionData);
      toast.success('Services section updated successfully!');
      fetchData(); // Re-fetch to ensure data consistency
    } catch (err) {
      console.error('Error updating services section:', err);
      setError('Failed to save services data. Please try again.');
      toast.error('Failed to save services data.');
    } finally {
      setSaving(false);
    }
  };

  const RenderStyleControlsGroup = ({ controlLabel, basePath, defaultStyles }) => {
    // Resolve values using sectionData and fallbacks from defaultStyles or hardcoded defaults
    const color = resolvePath(sectionData, `${basePath}Color`, (defaultStyles || {}).color || '#000000');
    const fontSize = resolvePath(sectionData, `${basePath}FontSize`, (defaultStyles || {}).fontSize || '16');
    const fontFamily = resolvePath(sectionData, `${basePath}FontFamily`, (defaultStyles || {}).fontFamily || 'Arial');
    const fontWeight = resolvePath(sectionData, `${basePath}FontWeight`, (defaultStyles || {}).fontWeight || '400');

    // Ensure fontSize is a number for the TextField type="number"
    const displayFontSize = (typeof fontSize === 'string' && fontSize.endsWith('px')) ? parseFloat(fontSize.replace('px','')) : fontSize;

    return (
      <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 2, borderTop: '1px solid #eee', pt: 2, mt: 1 }}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>{controlLabel} Styling</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" display="block" gutterBottom>Color</Typography>
          <Button variant="outlined" onClick={(e) => handleColorPickerOpen(e, `${basePath}Color`)} startIcon={<ColorLensIcon />} fullWidth>
            {color}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" display="block" gutterBottom>Font Size (px)</Typography>
          <TextField
            name={`${basePath}FontSize`}
            type="number"
            value={displayFontSize || ''} // Ensure value is not null/undefined for controlled component
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            size="small"
            InputProps={{ inputProps: { min: 8, max: 120 } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" display="block" gutterBottom>Font Family</Typography>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel shrink={!!fontFamily}>Family</InputLabel>
            <Select
              name={`${basePath}FontFamily`}
              value={resolvePath(sectionData, `${basePath}FontFamily`, (defaultStyles || {}).fontFamily || commonFontFamilies[0])}
              onChange={(event) => handleInputChange(event)} // Corrected: Ensure only event is passed
              label={`${controlLabel} Font Family`}
            >
              {commonFontFamilies.map((family) => (
                <MenuItem key={family} value={family}>{family}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" display="block" gutterBottom>Font Weight</Typography>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel shrink={!!fontWeight}>Weight</InputLabel>
            <Select
              name={`${basePath}FontWeight`}
              value={resolvePath(sectionData, `${basePath}FontWeight`, (defaultStyles || {}).fontWeight || '400')}
              onChange={(event) => handleInputChange(event)} // Corrected: Ensure only event is passed
              label={`${controlLabel} Font Weight`}
            >
              {commonFontWeights.map((weight) => (
                <MenuItem key={weight.value} value={weight.value}>{weight.label}</MenuItem>
              ))}
              <MenuItem value="700">700 (Bold)</MenuItem>
              <MenuItem value="800">800 (Extra Bold)</MenuItem>
              <MenuItem value="900">900 (Black)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>Loading Services Editor...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom component="h1">
          Edit Services Section
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={3}>
          {/* Main Section Content & Styling */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Main Section Content</Typography>
              <TextField
                label="Main Title"
                name="mainTitle"
                value={sectionData.mainTitle || ''}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Main Subtitle"
                name="mainSubtitle"
                value={sectionData.mainSubtitle || ''}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
                margin="normal"
              />
              <Divider sx={{ my: 2 }} />
              <RenderStyleControlsGroup controlLabel="Main Title" basePath="mainTitle" />
              <RenderStyleControlsGroup controlLabel="Main Subtitle" basePath="mainSubtitle" />
            </Paper>
          </Grid>

          {/* Detailed Service Description Section */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>Detailed Service Content</Typography>
              <TextField
                label="Detailed Service Title"
                name="title"
                value={sectionData.longServiceData?.title || ''}
                onChange={(e) => handleInputChange(e, 'longServiceData.title')}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Detailed Service Description"
                name="description"
                value={sectionData.longServiceData?.description || ''}
                onChange={(e) => handleInputChange(e, 'longServiceData.description')}
                fullWidth
                multiline
                rows={5}
                margin="normal"
              />
              <RenderStyleControlsGroup controlLabel="Detailed Service Title" basePath="longServiceData.title" />
              <RenderStyleControlsGroup controlLabel="Detailed Service Text" basePath="longServiceData.text" />
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Detailed Service Background & Image</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" gutterBottom>Background Color</Typography>
                  <Button variant="outlined" onClick={(e) => handleColorPickerOpen(e, 'longServiceData.backgroundColor')} startIcon={<ColorLensIcon />} fullWidth>{sectionData.longServiceData?.backgroundColor || '#FFFFFF'}</Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" gutterBottom>Upload Image</Typography>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="long-service-image-upload"
                    type="file"
                    onChange={handleLongServiceImageUpload}
                  />
                  <label htmlFor="long-service-image-upload">
                    <Button variant="contained" component="span" fullWidth disabled={saving}>
                      {saving ? 'Uploading...' : 'Upload Image'}
                    </Button>
                  </label>
                  {sectionData.longServiceData?.imageUrl && (
                    <Box mt={1} textAlign="center">
                      <img src={sectionData.longServiceData.imageUrl} alt="Preview" style={{ maxWidth: '100px', maxHeight: '100px', marginTop: '8px' }} />
                    </Box>
                  )}
                </Grid>
                 <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Image Alt Text"
                        name="imageAlt"
                        value={sectionData.longServiceData?.imageAlt || ''}
                        onChange={(e) => handleInputChange(e, 'longServiceData.imageAlt')}
                        variant="outlined"
                        size="small"
                        margin="normal"
                        helperText="Important for SEO and accessibility"
                    />
                </Grid>
              </Grid>
            </Paper>
          </Grid>


          {/* Global Styling Options */}
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>Global Styling Options</Typography>
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Main Section Titles</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField label="Main Title Color" name="mainTitleColor" value={sectionData.mainTitleColor || ''} onChange={handleInputChange} fullWidth margin="normal" helperText="e.g., #002D62, blue" sx={{ flexGrow: 1 }} />
                    <IconButton onClick={(e) => handleColorPickerOpen(e, 'mainTitleColor')}><ColorLensIcon /></IconButton>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Main Title Font Size" name="mainTitleFontSize" value={sectionData.mainTitleFontSize || ''} onChange={handleInputChange} fullWidth margin="normal" helperText="e.g., 3rem, 48px" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField label="Main Subtitle Color" name="mainSubtitleColor" value={sectionData.mainSubtitleColor || ''} onChange={handleInputChange} fullWidth margin="normal" helperText="e.g., #0057B8, steelblue" sx={{ flexGrow: 1 }} />
                    <IconButton onClick={(e) => handleColorPickerOpen(e, 'mainSubtitleColor')}><ColorLensIcon /></IconButton>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Main Subtitle Font Size" name="mainSubtitleFontSize" value={sectionData.mainSubtitleFontSize || ''} onChange={handleInputChange} fullWidth margin="normal" helperText="e.g., 1.5rem, 24px" />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Service Cards (Global Defaults)</Typography>
              <Grid container spacing={2} alignItems="flex-start">
                <RenderStyleControlsGroup controlLabel="Default Card Title" basePath="defaultCardTitle" />

                <RenderStyleControlsGroup controlLabel="Default Card Description" basePath="defaultCardDescription" />

                {/* Default Card Icon Styling */}
                <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 'medium', mt:2 }}>Default Card Icon</Typography></Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" display="block" gutterBottom>Icon Color</Typography>
                  <Button variant="outlined" onClick={(e) => handleColorPickerOpen(e, 'defaultCardIconColor')} startIcon={<ColorLensIcon />} fullWidth>{sectionData.defaultCardIconColor || '#000000'}</Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" display="block" gutterBottom>Icon Background Color</Typography>
                  <Button variant="outlined" onClick={(e) => handleColorPickerOpen(e, 'defaultCardIconBackgroundColor')} startIcon={<ColorLensIcon />} fullWidth>{sectionData.defaultCardIconBackgroundColor || '#FFFFFF'}</Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Service Cards */}
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom sx={{ mt: 3, display: 'flex', alignItems: 'center' }}>
              Service Cards
              <IconButton color="primary" onClick={addServiceCard} sx={{ ml: 1 }}>
                <AddCircleOutline />
              </IconButton>
            </Typography>
            {sectionData.serviceCards && sectionData.serviceCards.map((card, index) => (
              <Paper key={card.id || index} elevation={2} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">Service Card {index + 1}</Typography>
                  <IconButton color="error" onClick={() => removeServiceCard(index)}>
                    <DeleteOutline />
                  </IconButton>
                </Box>
                <FormControl fullWidth margin="normal">
                  <InputLabel id={`service-card-icon-label-${index}`}>Icon</InputLabel>
                  <Select
                    labelId={`service-card-icon-label-${index}`}
                    id={`service-card-icon-select-${index}`}
                    name="icon"
                    value={card.icon || ''}
                    label="Icon"
                    onChange={(e) => handleServiceCardChange(index, e)}
                  >
                    {iconOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Card Title"
                  name="title"
                  value={card.title || ''}
                  onChange={(e) => handleServiceCardChange(index, e)}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label="Card Description"
                  name="description"
                  value={card.description || ''}
                  onChange={(e) => handleServiceCardChange(index, e)}
                  fullWidth
                  multiline
                  rows={3}
                  margin="normal"
                />
                <TextField
                  label="Display Order"
                  name="displayOrder"
                  type="number"
                  value={card.displayOrder !== undefined ? card.displayOrder : index}
                  onChange={(e) => handleServiceCardChange(index, e)}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label="Display Order"
                  name="displayOrder"
                  type="number"
                  value={card.displayOrder !== undefined ? card.displayOrder : index}
                  onChange={(e) => handleServiceCardChange(index, e)}
                  fullWidth
                  margin="normal"
                  size="small"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={card.isVisible === undefined ? true : card.isVisible}
                      onChange={(e) => handleServiceCardChange(index, e)}
                      name="isVisible"
                    />
                  }
                  label="Visible"
                />

                <RenderStyleControlsGroup
                  controlLabel="Card Title"
                  basePath={`serviceCards.${index}.title`}
                  defaultStyles={{
                    color: sectionData.defaultCardTitleColor,
                    fontSize: sectionData.defaultCardTitleFontSize,
                    fontFamily: sectionData.defaultCardTitleFontFamily,
                    fontWeight: sectionData.defaultCardTitleFontWeight
                  }}
                />

                <RenderStyleControlsGroup
                  controlLabel="Card Description"
                  basePath={`serviceCards.${index}.description`}
                  defaultStyles={{
                    color: sectionData.defaultCardDescriptionColor,
                    fontSize: sectionData.defaultCardDescriptionFontSize,
                    fontFamily: sectionData.defaultCardDescriptionFontFamily,
                    fontWeight: sectionData.defaultCardDescriptionFontWeight
                  }}
                />

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>Card Icon Styling</Typography>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" display="block" gutterBottom>Icon Color</Typography>
                    <Button variant="outlined" onClick={(e) => handleColorPickerOpen(e, `serviceCards.${index}.iconColor`)} startIcon={<ColorLensIcon />} fullWidth>{card.iconColor || sectionData.defaultCardIconColor}</Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" display="block" gutterBottom>Icon Background Color</Typography>
                    <Button variant="outlined" onClick={(e) => handleColorPickerOpen(e, `serviceCards.${index}.iconBackgroundColor`)} startIcon={<ColorLensIcon />} fullWidth>{card.iconBackgroundColor || sectionData.defaultCardIconBackgroundColor}</Button>
                  </Grid>
                </Grid>

              </Paper>
            ))}
          </Grid>

          <Grid item xs={12} sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving || loading}
              fullWidth
              size="large"
            >
              {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {showColorPicker && currentColorField && (
        <Popover
          open={showColorPicker}
          anchorEl={colorPickerAnchorEl}
          onClose={handleColorPickerClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <ClickAwayListener onClickAway={handleColorPickerClose}>
            <Paper elevation={8} sx={{ p: 1 }}>
              <ChromePicker
                color={resolvePath(sectionData, currentColorField) || '#FFFFFF'}
                onChangeComplete={handleColorChange}
                disableAlpha
              />
              <Button onClick={handleColorPickerClose} variant="contained" size="small" fullWidth sx={{ mt: 1 }}>Close</Button>
            </Paper>
          </ClickAwayListener>
        </Popover>
      )}
    </Container>
  );


}
export default ServicesSectionEditor;